import { useMemo, useRef } from "react";
import type { EditingState, Step } from "~/features/orders/types";
import { liveTransferOutcomeOptions, defaultOutcomeOptions } from "~/features/orders/constants";
// storeApi removed - use API calls directly
import { WindowSelect } from "~/shared/components/WindowSelect";

type EditModalProps = Readonly<{
  editing: EditingState;
  step: Step;
  isLiveTransferContext: boolean;
  onCancel: () => void;
  onSaveStep2: () => void;
  onSaveStep3: () => void;
  onUpdateOrders: () => void;
  onUpdateEditing: (updates: Partial<EditingState>) => void;
}>;

export function EditModal({
  editing,
  step,
  isLiveTransferContext,
  onCancel,
  onSaveStep2,
  onSaveStep3,
  onUpdateOrders,
  onUpdateEditing,
}: EditModalProps) {
  // const api = storeApi();
  const modalOutcomeOptions = isLiveTransferContext ? liveTransferOutcomeOptions : defaultOutcomeOptions;

  const minDate = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const maxDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const dateTypingRef = useRef(false);

  const handleSave = () => {
    if (!editing) return;
    if (step === 2) {
      onSaveStep2();
    } else if (step === 3) {
      onSaveStep3();
    } else {
      if (editing.outcome === 'cancelled' && !editing.cancellationReason?.trim()) {
        alert('Enter a cancellation reason');
        return;
      }
      // TODO: Call backend API
      /*
      api.resolveManual(
        editing.order.id as number,
        editing.outcome as any,
        editing.window ?? editing.order.preferred_window ?? null,
        editing.outcome === 'reschedule' ? editing.date : undefined,
        editing.outcome === 'cancelled' ? editing.cancellationReason?.trim() : undefined
      );
      */
      onUpdateOrders();
      onCancel();
    }
  };

  return (
    <dialog
      open
      aria-label="Edit outcome"
      style={{
        margin: 0,
        padding: 0,
        border: 'none',
        background: 'transparent',
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
      }}
    >
      <div
        className="modal-content"
        style={{ background: '#fff', borderRadius: 12, width: 480, position: 'relative', zIndex: 1 }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div style={{ padding: 16, borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 600 }}>Edit Outcome</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {editing.order.sm_number} — {editing.order.patient_name}
            </div>
          </div>
          <button onClick={onCancel} style={{ background: 'transparent', border: 'none', fontSize: 22 }}>
            ×
          </button>
        </div>
        <div style={{ padding: 16, display: 'grid', gap: 12 }}>
          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Outcome</span>
            <select
              value={editing.outcome}
              onChange={e => {
                const nextOutcome = e.target.value;
                onUpdateEditing({
                  outcome: nextOutcome,
                  cancellationReason: nextOutcome === 'cancelled' ? (editing.cancellationReason ?? '') : undefined,
                });
              }}
            >
              {modalOutcomeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          {(
            (isLiveTransferContext && (editing.outcome === 'confirmed' || editing.outcome === 'reschedule')) ||
            (!isLiveTransferContext && (editing.outcome === 'confirmed' || editing.outcome === 'alt_window' || editing.outcome === 'reschedule'))
          ) && (
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Window</span>
                <WindowSelect value={editing.window} onChange={(w) => onUpdateEditing({ window: w })} />
              </label>
            )}
          {editing.outcome === 'reschedule' && (
            <div style={{ display: 'grid', gap: 6 }}>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>New Date</span>
              <input
                type="date"
                value={editing.date}
                onKeyDown={() => { dateTypingRef.current = true; }}
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) {
                    if (!dateTypingRef.current) onUpdateEditing({ date: '' });
                    dateTypingRef.current = false;
                    return;
                  }
                  dateTypingRef.current = false;
                  const parts = v.split('-');
                  let sanitized = v;
                  if (parts.length === 3) {
                    const month = parts[1] === '00' ? '01' : parts[1];
                    const day = parts[2] === '00' ? '01' : parts[2];
                    sanitized = `${parts[0]}-${month}-${day}`;
                  }
                  if (sanitized < minDate) { onUpdateEditing({ date: minDate }); return; }
                  if (sanitized > maxDate) { onUpdateEditing({ date: maxDate }); return; }
                  onUpdateEditing({ date: sanitized });
                }}
                onBlur={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  if (v < minDate) onUpdateEditing({ date: minDate });
                  else if (v > maxDate) onUpdateEditing({ date: maxDate });
                }}
                min={minDate}
                max={maxDate}
                style={{ outline: 'none' }}
              />
            </div>
          )}
          {editing.outcome === 'cancelled' && (
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Cancellation Reason</span>
              <textarea
                value={editing.cancellationReason ?? ''}
                onChange={e => onUpdateEditing({ cancellationReason: e.target.value })}
                style={{ minHeight: 64 }}
              />
            </label>
          )}
        </div>
        <div style={{ padding: 16, borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="secondary" onClick={onCancel}>
            Cancel
          </button>
          <button className="primary" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </dialog>
  );
}
